import { ReactNode, useMemo, useState } from 'react';
import { LuChevronDown, LuChevronUp, LuCircleCheck, LuSquarePen } from 'react-icons/lu';

import { Role } from '@ndvr/auth-api';
import { Account, BillingGroup, FeeSchedule, GUID } from '@ndvr/billing-api';

import { ConfirmModal } from '@/components/ConfirmModal';
import { Spinner } from '@/components/Spinner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { EllipsisDropdownMenuTrigger } from '@/components/ui/dropdown-menu-triggers';
import { formatCurrency, formatCurrencyShort, titleCase, useRequiredParams } from 'utils';
import { useUserHasRole } from 'views/admin/dev-queries';
import {
    useBillingGroupAccountNameMap,
    useBillingGroupActions,
} from 'views/billing/billing-groups/billing-group-hooks';
import { useBillingGroupsForAccounts } from 'views/billing/billing-groups/billing-group-queries';
import { BillingGroupAccountsTable } from 'views/billing/billing-groups/BillingGroupAccountsTable';
import { CreateBillingGroupDialog } from 'views/billing/billing-groups/CreateBillingGroupDialog';
import { useListAccountGroupProfiles } from 'views/portfolio/account-group-queries';

const bpsToPercent = (annualBps: number) => `${(annualBps / 100).toFixed(2)}%`;

const DetailField = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className="flex min-w-[8rem] flex-col gap-1">
        <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">{label}</span>
        <div className="text-sm">{children}</div>
    </div>
);

// Read-only summary of a fee schedule's terms. A fee schedule always has rate
// tiers; a flat fee is an optional add-on billed in addition to them, not instead.
const FeeScheduleDetails = ({ feeSchedule }: { feeSchedule: FeeSchedule }) => {
    const { billingCycle, billingTiming, feeTiers, billAtSingleBps, flatFeeConfig } = feeSchedule;
    const cadence = `${titleCase(billingCycle)} in ${billingTiming.toLowerCase()}`;

    const tiers = [...feeTiers].sort((a, b) => Number(a.tierStart) - Number(b.tierStart));

    const tierLabel = (start: number, next: number | null, isFirst: boolean): string => {
        if (billAtSingleBps) {
            if (isFirst) return next != null ? `Up to ${formatCurrencyShort(next)}` : 'Any balance';
            return next != null
                ? `${formatCurrencyShort(start)} – ${formatCurrencyShort(next)}`
                : `Over ${formatCurrencyShort(start)}`;
        }
        if (isFirst) return next != null ? `First ${formatCurrencyShort(next)}` : 'All assets';
        return next != null
            ? `${formatCurrencyShort(start)} – ${formatCurrencyShort(next)}`
            : `Above ${formatCurrencyShort(start)}`;
    };

    return (
        <div className="bg-muted/40 flex flex-wrap gap-x-12 gap-y-4 border-t border-gray-200 px-6 py-4">
            <DetailField label="Cadence">{cadence}</DetailField>
            <DetailField label="Fee type">
                Tiered AUM
                {/* Matches the editor's "Bill at single bps" flag; it bills the whole balance at
                    one tier's rate instead of blending across tiers. */}
                {billAtSingleBps && <span className="text-muted-foreground"> · single bps</span>}
                {flatFeeConfig && <span className="text-muted-foreground"> + flat fee</span>}
            </DetailField>
            <DetailField label="Rate tiers">
                <div className="flex flex-col gap-0.5">
                    {tiers.map((tier, i) => {
                        const isFirst = i === 0;
                        const start = Number(tier.tierStart);
                        const next = tiers[i + 1] ? Number(tiers[i + 1].tierStart) : null;
                        const label = tierLabel(start, next, isFirst);
                        return (
                            <div key={i} className="flex items-baseline justify-between gap-6">
                                <span className="text-muted-foreground">{label}</span>
                                <span className="font-medium tabular-nums">{bpsToPercent(Number(tier.annualBps))}</span>
                            </div>
                        );
                    })}
                </div>
            </DetailField>
            {flatFeeConfig && (
                <DetailField label="Flat fee">
                    <span>{formatCurrency(Number(flatFeeConfig.annualFlatFeeAmount))} / year</span>
                </DetailField>
            )}
        </div>
    );
};

interface BillingGroupCardProps {
    billingGroup: BillingGroup;
    nameByGUID: Map<string, string>;
    namesLoading: boolean;
    canManage: boolean;
    onEdit: (billingGroup: BillingGroup) => void;
    onActivate: (billingGroup: BillingGroup) => void;
    onDeactivate: (billingGroup: BillingGroup) => void;
}

const BillingGroupCard = ({
    billingGroup,
    nameByGUID,
    namesLoading,
    canManage,
    onEdit,
    onActivate,
    onDeactivate,
}: BillingGroupCardProps) => {
    const [detailsOpen, setDetailsOpen] = useState(false);
    const count = billingGroup.accounts.length;

    return (
        <Card className="overflow-hidden">
            <div className="flex items-start justify-between px-6 py-4">
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                        <span className="font-medium">{billingGroup.billingGroupName}</span>
                        {billingGroup.active ? (
                            <Badge variant="mint" className="gap-1">
                                <LuCircleCheck className="h-3.5 w-3.5" /> Active
                            </Badge>
                        ) : (
                            <Badge variant="secondary">Inactive</Badge>
                        )}
                    </div>
                    <div className="text-muted-foreground flex flex-wrap items-center gap-x-2 text-sm">
                        <span>
                            Fee schedule:{' '}
                            <span className="text-foreground font-medium">
                                {billingGroup.feeSchedule.feeScheduleName ?? '—'}
                            </span>
                        </span>
                        <button
                            type="button"
                            className="text-primary inline-flex items-center gap-0.5 font-medium hover:underline"
                            aria-expanded={detailsOpen}
                            onClick={() => setDetailsOpen((open) => !open)}
                        >
                            Details
                            {detailsOpen ? <LuChevronUp className="h-4 w-4" /> : <LuChevronDown className="h-4 w-4" />}
                        </button>
                        <span className="text-muted-foreground/70">
                            · {count} {count === 1 ? 'account' : 'accounts'}
                        </span>
                    </div>
                </div>
                {canManage && (
                    <div className="flex items-center gap-2">
                        <Button variant="outline2" size="sm" className="px-3" onClick={() => onEdit(billingGroup)}>
                            <LuSquarePen className="h-4 w-4" /> Edit
                        </Button>
                        <DropdownMenu>
                            <EllipsisDropdownMenuTrigger />
                            <DropdownMenuContent align="end">
                                {billingGroup.active ? (
                                    <DropdownMenuItem
                                        className="text-destructive focus:text-destructive"
                                        onClick={() => onDeactivate(billingGroup)}
                                    >
                                        Deactivate
                                    </DropdownMenuItem>
                                ) : (
                                    <DropdownMenuItem onClick={() => onActivate(billingGroup)}>
                                        Activate
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                )}
            </div>

            {detailsOpen && <FeeScheduleDetails feeSchedule={billingGroup.feeSchedule} />}

            <div className="border-t border-gray-200">
                <BillingGroupAccountsTable
                    accounts={billingGroup.accounts}
                    nameByGUID={nameByGUID}
                    namesLoading={namesLoading}
                    canManage={canManage}
                    className="px-6 py-3"
                />
            </div>
        </Card>
    );
};

export const HouseholdBillingGroupsTab = () => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    // Only the account membership is needed here, so use the lighter profile query
    // rather than the AUM-computing variant.
    const { data: accountGroupProfiles = [], isLoading: profilesLoading } =
        useListAccountGroupProfiles(globalAccountGUID);
    const { data: canManage = false } = useUserHasRole(Role.CHIEF_COMPLIANCE_OFFICER);

    const accountGUIDs = useMemo<GUID<Account>[]>(
        () => accountGroupProfiles.flatMap((profile) => profile.accounts.map((account) => account.accountGUID)),
        [accountGroupProfiles],
    );

    const { data: billingGroups = [], isLoading: billingGroupsLoading } = useBillingGroupsForAccounts(accountGUIDs);
    const { nameByGUID, namesLoading } = useBillingGroupAccountNameMap(billingGroups);
    const {
        activate,
        deactivateMutation,
        editingItem,
        dialogOpen,
        setDialogOpen,
        openEdit,
        deactivatingGUIDs,
        requestDeactivate,
        cancelDeactivate,
        confirmDeactivate,
    } = useBillingGroupActions();

    const sortedGroups = useMemo(
        () =>
            [...billingGroups].sort((a, b) =>
                a.billingGroupName.localeCompare(b.billingGroupName, undefined, { sensitivity: 'base' }),
            ),
        [billingGroups],
    );

    const loading = profilesLoading || billingGroupsLoading;

    return (
        <div className="flex flex-col gap-6">
            <h2 className="text-cocoa-1000 text-xl">Billing Groups</h2>

            {loading ? (
                <div className="flex justify-center py-12">
                    <Spinner size="sm" />
                </div>
            ) : sortedGroups.length === 0 ? (
                <p className="text-muted-foreground py-8 text-center text-sm">
                    This household&apos;s accounts are not in any billing groups.
                </p>
            ) : (
                <div className="flex flex-col gap-4">
                    {sortedGroups.map((group) => (
                        <BillingGroupCard
                            key={group.billingGroupGUID}
                            billingGroup={group}
                            nameByGUID={nameByGUID}
                            namesLoading={namesLoading}
                            canManage={canManage}
                            onEdit={openEdit}
                            onActivate={(bg) => activate([bg.billingGroupGUID])}
                            onDeactivate={(bg) => requestDeactivate([bg.billingGroupGUID])}
                        />
                    ))}
                </div>
            )}

            {canManage && editingItem && (
                <CreateBillingGroupDialog
                    tenantGUID={editingItem.tenantGUID}
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                    billingGroup={editingItem}
                />
            )}

            <ConfirmModal
                open={!!deactivatingGUIDs}
                onOpenChange={(open) => !open && cancelDeactivate()}
                title="Deactivate Billing Group"
                description="Are you sure you want to deactivate this billing group?"
                confirmText="Deactivate"
                confirmVariant="destructive"
                onConfirm={confirmDeactivate}
                loading={deactivateMutation.isPending}
                closeOnConfirm={false}
            />
        </div>
    );
};
